import { forwardRef } from 'react';
import { formatCurrency } from '../../../utils/format';

const PAYMENT_METHOD_LABELS = {
    ACH: 'ACH Transfer',
    BANK_TRANSFER: 'Bank Transfer',
    CARD: 'Card',
    CASH: 'Cash',
    CASH_ON_DELIVERY: 'Cash on Delivery',
    CHECK: 'Cheque',
    CHEQUE: 'Cheque',
    COD: 'Cash on Delivery',
    CREDIT_CARD: 'Credit Card',
    CRYPTO: 'Crypto',
    DEBIT_CARD: 'Debit Card',
    NET_BANKING: 'Net Banking',
    ONLINE: 'Online Payment',
    OTHER: 'Other',
    PAYPAL: 'PayPal',
    RAZORPAY: 'Razorpay',
    STRIPE: 'Stripe',
    UPI: 'UPI',
    WALLET: 'Wallet',
    WIRE: 'Wire Transfer',
    ZELLE: 'Zelle'
};

const BLANK_PAYMENT_VALUES = new Set(['', 'N/A', 'NA', 'NONE', 'NULL', 'UNDEFINED', '-', '—']);

const formatPaymentMethod = (value) => {
    if (!value) return 'Cash';
    const upper = String(value).trim().toUpperCase().replace(/[\s-]/g, '_');
    if (BLANK_PAYMENT_VALUES.has(upper)) return 'Cash';
    return PAYMENT_METHOD_LABELS[upper]
        || String(value)
            .trim()
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatAddressValue = (value, { multiline = false } = {}) => {
    if (!value) return '';
    if (typeof value !== 'string') return '';
    const normalized = value
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
    return multiline ? normalized.join('\n') : normalized.join(', ');
};

const sans = '"Inter", system-ui, -apple-system, "Segoe UI", Arial, sans-serif';
const mono = '"JetBrains Mono", "Fira Mono", "Courier New", Courier, monospace';

const DARK = '#0f172a';
const MID = '#334155';
const MUTED = '#64748b';
const SUBTLE = '#94a3b8';
const LIGHT = '#f8fafc';
const RULE = '#e2e8f0';
const RULE2 = '#cbd5e1';
const WHITE = '#ffffff';

const Label = ({ children, style = {} }) => (
    <div
        style={{
            fontFamily: sans,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '1.8px',
            textTransform: 'uppercase',
            color: SUBTLE,
            marginBottom: 7,
            ...style
        }}
    >
        {children}
    </div>
);

const HRule = ({ mt = 0, mb = 0, color = RULE }) => (
    <div style={{ height: 1, background: color, marginTop: mt, marginBottom: mb }} />
);

const ReceiptDocument = forwardRef((props, ref) => {
    const {
        documentLabel = 'Receipt',
        receiptNumber = '',
        customer = '',
        customerEmail = '',
        customerPhone = '',
        status = '',
        paymentMethod = '',
        paymentStatus = '',
        transactionId = '',
        bookingDate = '',
        bookingTime = '',
        bookingEndTime = '',
        bookingDuration = '',
        bookingTimeZone = '',
        items = [],
        subtotal = 0,
        discountAmount = 0,
        discountLabel = '',
        total = 0,
        memo = '',
        notes = '',
        companyName = '',
        companyAddress = '',
        companyEmail = '',
        companyPhone = '',
        companyLogo = null,
        companyContext = 'US',
        themeVariant = 'retail'
    } = props;

    const fmt = (value) => formatCurrency(value, companyContext);
    const pmLabel = formatPaymentMethod(paymentMethod);
    const normalizedCompanyAddress = formatAddressValue(companyAddress, { multiline: true });
    const hasHeaderLogo = Boolean(companyLogo);
    const isFlexOffice = themeVariant === 'flexOffice';
    const ACCENT = isFlexOffice ? 'var(--flex-primary, #2563eb)' : '#ea6c00';
    const ACCENT_BG = isFlexOffice ? 'var(--flex-primary-soft, #eff6ff)' : '#fff7ed';
    const ACCENT_BORDER = isFlexOffice ? 'var(--flex-primary-muted, #93c5fd)' : '#fdba74';
    const HEADER_STOP = isFlexOffice ? '#1e3a8a' : '#1e293b';
    const bookingTimeStr = [bookingTime, bookingEndTime].filter(Boolean).join(' - ');

    return (
        <div
            ref={ref}
            style={{
                width: 794,
                minHeight: 'auto',
                background: WHITE,
                boxSizing: 'border-box',
                fontFamily: sans
            }}
        >
            <div
                style={{
                    height: 5,
                    background: `linear-gradient(90deg, ${DARK} 0%, ${HEADER_STOP} 45%, ${ACCENT} 100%)`
                }}
            />

            <div style={{ padding: '40px 52px 0' }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: 28
                    }}
                >
                    <div style={{ flex: 1, maxWidth: 340 }}>
                        {hasHeaderLogo ? (
                            <div>
                                <img
                                    src={companyLogo}
                                    alt="logo"
                                    crossOrigin="anonymous"
                                    loading="eager"
                                    decoding="sync"
                                    style={{ height: 'auto', maxHeight: 50, maxWidth: 200, width: 'auto', display: 'block' }}
                                />
                            </div>
                        ) : (
                            <div
                                style={{
                                    fontSize: 24,
                                    fontWeight: 800,
                                    color: DARK,
                                    letterSpacing: '-0.5px',
                                    lineHeight: 1.15,
                                    marginBottom: 12
                                }}
                            >
                                {companyName || 'Your Company'}
                            </div>
                        )}
                    </div>

                    <div style={{ textAlign: 'right', minWidth: 200 }}>
                        <div
                            style={{
                                fontSize: 34,
                                fontWeight: 900,
                                color: DARK,
                                textTransform: 'uppercase',
                                letterSpacing: '-1px',
                                lineHeight: 1,
                                marginBottom: 18
                            }}
                        >
                            {documentLabel}
                        </div>
                        {receiptNumber && (
                            <div style={{ fontSize: 11, color: MUTED, marginBottom: 5, display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                                <span>No.</span>
                                <span style={{ fontFamily: mono, fontWeight: 700, color: DARK, fontSize: 13 }}>{receiptNumber}</span>
                            </div>
                        )}
                    </div>
                </div>

                <HRule mb={24} color={RULE2} />

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        marginBottom: 28,
                        border: `1.5px solid ${RULE2}`,
                        borderRadius: 10,
                        overflow: 'hidden',
                        background: WHITE
                    }}
                >
                    <div style={{ padding: '20px 22px' }}>
                        <Label>Bill To</Label>
                        <div style={{ fontSize: 14, fontWeight: 700, color: DARK, marginBottom: 6, lineHeight: 1.3 }}>
                            {customer || '—'}
                        </div>
                        {customerEmail && <div style={{ fontSize: 11, color: MUTED, marginBottom: 3, lineHeight: 1.6 }}>{customerEmail}</div>}
                        {customerPhone && <div style={{ fontSize: 11, color: MUTED, lineHeight: 1.6 }}>{customerPhone}</div>}
                    </div>

                    <div
                        style={{
                            padding: '20px 22px',
                            borderLeft: `1.5px solid ${RULE2}`,
                            borderRight: `1.5px solid ${RULE2}`
                        }}
                    >
                        <Label>From</Label>
                        <div style={{ fontSize: 14, fontWeight: 700, color: DARK, marginBottom: 6, lineHeight: 1.3 }}>
                            {companyName || '—'}
                        </div>
                        {companyEmail && <div style={{ fontSize: 11, color: MUTED, marginBottom: 3, lineHeight: 1.6 }}>{companyEmail}</div>}
                        {companyPhone && <div style={{ fontSize: 11, color: MUTED, marginBottom: 5, lineHeight: 1.6 }}>{companyPhone}</div>}
                        {normalizedCompanyAddress && (
                            <div style={{ fontSize: 11, color: MUTED, lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                                {normalizedCompanyAddress}
                            </div>
                        )}
                    </div>

                    <div style={{ padding: '20px 22px', background: LIGHT }}>
                        <Label>Payment</Label>
                        <div style={{ fontSize: 13, fontWeight: 700, color: DARK, marginBottom: 8 }}>{pmLabel}</div>
                        {status && (
                            <div style={{ fontSize: 11, color: MUTED, marginBottom: 3, lineHeight: 1.6 }}>
                                <span style={{ color: SUBTLE }}>Order Status: </span>{status}
                            </div>
                        )}
                        {paymentStatus && (
                            <div style={{ fontSize: 11, color: MUTED, marginBottom: 3, lineHeight: 1.6 }}>
                                <span style={{ color: SUBTLE }}>Status: </span>{paymentStatus}
                            </div>
                        )}
                        {transactionId && (
                            <div style={{ fontSize: 10, color: MUTED, marginTop: 4, fontFamily: mono, lineHeight: 1.55, wordBreak: 'break-word' }}>
                                <span style={{ color: SUBTLE }}>Ref ID: </span>{transactionId}
                            </div>
                        )}
                        {(bookingDate || bookingTimeStr || bookingTimeZone) && (
                            <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${RULE}`, fontSize: 11, color: MUTED, lineHeight: 1.7 }}>
                                {bookingDate && <div>{bookingDate}</div>}
                                {bookingTimeStr && <div>{bookingTimeStr}</div>}
                                {bookingTimeZone && <div>{bookingTimeZone}</div>}
                            </div>
                        )}
                        {bookingDuration && (
                            <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>Duration: {bookingDuration}</div>
                        )}
                    </div>
                </div>

                <div
                    style={{
                        border: `1.5px solid ${RULE2}`,
                        borderRadius: 10,
                        overflow: 'hidden',
                        marginBottom: 28
                    }}
                >
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(0,1fr) 64px 110px 110px',
                            background: LIGHT,
                            borderBottom: `1.5px solid ${RULE2}`,
                            padding: '11px 20px'
                        }}
                    >
                        {[
                            { label: 'Description', align: 'left' },
                            { label: 'Qty', align: 'right' },
                            { label: 'Unit Rate', align: 'right' },
                            { label: 'Amount', align: 'right' }
                        ].map(({ label, align }) => (
                            <div
                                key={label}
                                style={{
                                    fontFamily: sans,
                                    fontSize: 9,
                                    fontWeight: 700,
                                    letterSpacing: '1.6px',
                                    textTransform: 'uppercase',
                                    color: MUTED,
                                    textAlign: align
                                }}
                            >
                                {label}
                            </div>
                        ))}
                    </div>

                    {items.length === 0 ? (
                        <div style={{ padding: '28px 20px', textAlign: 'center', fontSize: 12, color: MUTED, fontStyle: 'italic' }}>
                            No items
                        </div>
                    ) : (
                        items.map((item, index) => {
                            const qty = parseFloat(item.qty) || 0;
                            const rate = parseFloat(item.rate) || 0;
                            const amount = Math.round(qty * rate * 100) / 100;

                            return (
                                <div
                                    key={item.id || index}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'minmax(0,1fr) 64px 110px 110px',
                                        padding: '14px 20px',
                                        background: index % 2 === 0 ? WHITE : LIGHT,
                                        borderTop: `1px solid ${RULE}`,
                                        alignItems: 'center'
                                    }}
                                >
                                    <div style={{ paddingRight: 16 }}>
                                        <div style={{ fontSize: 13, color: DARK, fontWeight: 600, lineHeight: 1.4 }}>
                                            {item.desc || 'Item'}
                                        </div>
                                        {item.description && item.description !== item.desc && (
                                            <div style={{ fontSize: 11, color: MUTED, marginTop: 3, lineHeight: 1.55 }}>
                                                {item.description}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ fontSize: 12, color: MUTED, textAlign: 'right', fontFamily: mono }}>{qty}</div>
                                    <div style={{ fontFamily: mono, fontSize: 12, color: MUTED, textAlign: 'right' }}>{fmt(rate)}</div>
                                    <div style={{ fontFamily: mono, fontSize: 13, color: DARK, textAlign: 'right', fontWeight: 700 }}>
                                        {fmt(amount)}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 32 }}>
                    <div
                        style={{
                            width: 284,
                            border: `1.5px solid ${RULE2}`,
                            borderRadius: 10,
                            overflow: 'hidden'
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '12px 18px',
                                borderBottom: `1px solid ${RULE}`,
                                background: WHITE
                            }}
                        >
                            <span style={{ fontSize: 12, color: MUTED }}>Subtotal</span>
                            <span style={{ fontFamily: mono, fontSize: 12, color: MID, fontWeight: 600 }}>{fmt(subtotal)}</span>
                        </div>

                        {discountAmount > 0 && (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '12px 18px',
                                    borderBottom: `1px solid ${RULE}`,
                                    background: WHITE
                                }}
                            >
                                <span style={{ fontSize: 12, color: ACCENT }}>
                                    Discount{discountLabel ? ` (${discountLabel})` : ''}
                                </span>
                                <span style={{ fontFamily: mono, fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
                                    -{fmt(discountAmount)}
                                </span>
                            </div>
                        )}

                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '15px 18px',
                                background: ACCENT_BG,
                                borderTop: `2px solid ${ACCENT_BORDER}`
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    letterSpacing: '2px',
                                    textTransform: 'uppercase',
                                    color: ACCENT
                                }}
                            >
                                Total
                            </span>
                            <span
                                style={{
                                    fontFamily: mono,
                                    fontSize: 22,
                                    fontWeight: 900,
                                    color: DARK,
                                    letterSpacing: '-0.5px'
                                }}
                            >
                                {fmt(total)}
                            </span>
                        </div>
                    </div>
                </div>

                {(memo || notes) && (
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: memo && notes ? '1fr 1fr' : '1fr',
                            gap: 16,
                            marginBottom: 32
                        }}
                    >
                        {[
                            memo && { label: 'Memo', text: memo },
                            notes && { label: 'Notes', text: notes }
                        ]
                            .filter(Boolean)
                            .map((block) => (
                                <div
                                    key={block.label}
                                    style={{
                                        border: `1.5px solid ${RULE2}`,
                                        borderRadius: 10,
                                        padding: '16px 18px',
                                        background: LIGHT
                                    }}
                                >
                                    <Label style={{ marginBottom: 6 }}>{block.label}</Label>
                                    <div style={{ fontSize: 12, color: MID, lineHeight: 1.75 }}>{block.text}</div>
                                </div>
                            ))}
                    </div>
                )}

                <HRule mt={4} mb={18} color={RULE} />
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingBottom: 32
                    }}
                >
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', color: SUBTLE }}>
                        Thank you for your business
                    </div>
                    {receiptNumber && (
                        <div style={{ fontFamily: mono, fontSize: 9, color: SUBTLE, letterSpacing: '0.5px' }}>
                            {receiptNumber}
                        </div>
                    )}
                </div>
            </div>

            <div style={{ height: 5, background: `linear-gradient(90deg, ${ACCENT} 0%, ${DARK} 100%)` }} />
        </div>
    );
});

ReceiptDocument.displayName = 'ReceiptDocument';

export default ReceiptDocument;
